import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import WarehousePageHero from '../../landing/WarehousePageHero';

export type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>>;

export interface MetricItem {
  value: string;
  label: string;
}

export interface HeroAction {
  href: string;
  label: string;
  variant?: 'primary' | 'secondary';
}

export interface HeroPanelItem {
  icon: IconType;
  title: string;
  description: string;
}

export interface FaqItem {
  question: string;
  answer: React.ReactNode;
}

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.75 },
};

const renderHighlightedTitle = (title: string, highlight?: string) => {
  if (!highlight) return title;

  const index = title.indexOf(highlight);
  if (index === -1) return title;

  const before = title.slice(0, index);
  const after = title.slice(index + highlight.length);

  return (
    <>
      {before}
      <span className="bg-gradient-to-r from-[#7dd3fc] via-[#38bdf8] to-[#60a5fa] bg-clip-text text-transparent">
        {highlight}
      </span>
      {after}
    </>
  );
};

export const SectionHeading: React.FC<{
  eyebrow: string;
  title: string;
  description: string;
  align?: 'left' | 'center';
}> = ({ eyebrow, title, description, align = 'center' }) => (
  <motion.div
    {...reveal}
    className={`mb-14 ${align === 'center' ? 'mx-auto max-w-3xl text-center' : 'max-w-3xl text-left'}`}
  >
    <span className="eyebrow-pill">{eyebrow}</span>
    <h2 className="mt-5 text-[30px] font-bold tracking-tight text-slate-950 sm:text-[38px]">{title}</h2>
    <p className="mt-4 text-base leading-8 text-slate-600 sm:text-lg">{description}</p>
  </motion.div>
);

export const MetricGrid: React.FC<{
  items: MetricItem[];
  tone?: 'dark' | 'light';
}> = ({ items, tone = 'dark' }) => (
  <div className={`grid gap-4 ${items.length > 3 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-3'}`}>
    {items.map((item) => (
      <div
        key={item.label}
        className={
          tone === 'dark'
            ? 'rounded-[24px] border border-white/[0.12] bg-white/[0.08] px-4 py-4 backdrop-blur-xl'
            : 'rounded-[24px] border border-slate-200/80 bg-white px-4 py-4 shadow-[0_18px_45px_-36px_rgba(15,23,42,0.4)]'
        }
      >
        <p className={`font-display text-2xl font-bold sm:text-3xl ${tone === 'dark' ? 'text-white' : 'text-slate-950'}`}>
          {item.value}
        </p>
        <p className={`mt-2 text-sm ${tone === 'dark' ? 'text-slate-300' : 'text-slate-500'}`}>{item.label}</p>
      </div>
    ))}
  </div>
);

const StandardPageHero: React.FC<{
  eyebrow: string;
  title: string;
  description: string;
  highlight?: string;
  heroIcon: IconType;
  accentIcon?: IconType;
  actions?: HeroAction[];
  badges?: string[];
  metrics?: MetricItem[];
  panelEyebrow?: string;
  panelTitle?: string;
  panelDescription?: string;
  panelItems?: HeroPanelItem[];
}> = ({
  eyebrow,
  title,
  description,
  highlight,
  heroIcon: HeroIcon,
  accentIcon: AccentIcon,
  actions = [],
  badges = [],
  metrics = [],
  panelEyebrow,
  panelTitle,
  panelDescription,
  panelItems = [],
}) => (
  <section className="relative overflow-hidden bg-slate-950 pb-24 pt-32 text-white sm:pt-36">
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(56,92,242,0.26),transparent_36%),radial-gradient(circle_at_75%_15%,rgba(14,158,213,0.22),transparent_30%),linear-gradient(135deg,rgba(2,6,23,0.96),rgba(15,23,42,0.9)_42%,rgba(2,6,23,0.98))]" />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
      <HeroIcon className="absolute left-8 top-20 h-28 w-28 text-white/10 sm:left-16 sm:h-36 sm:w-36" />
      {AccentIcon ? (
        <AccentIcon className="absolute bottom-12 right-8 h-24 w-24 text-white/10 sm:right-16 sm:h-32 sm:w-32" />
      ) : null}
    </div>

    <div className="relative container-custom grid items-center gap-12 lg:grid-cols-[1.02fr_0.98fr]">
      <motion.div {...reveal} className="text-left">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.08] px-5 py-2.5 text-sm font-medium text-white/90 backdrop-blur-xl">
          <span className="h-2 w-2 rounded-full bg-[#0E9ED5]" />
          {eyebrow}
        </span>
        <h1 className="mt-7 max-w-3xl font-display text-5xl font-bold leading-[1.02] tracking-[-0.04em] text-white sm:text-6xl">
          {renderHighlightedTitle(title, highlight)}
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg">{description}</p>

        {actions.length ? (
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            {actions.map((action) => (
              <Link
                key={`${action.href}-${action.label}`}
                to={action.href}
                className={
                  action.variant === 'secondary'
                    ? 'inline-flex items-center justify-center rounded-2xl border border-white/[0.12] bg-white/[0.08] px-7 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-white/90 backdrop-blur-xl transition-colors hover:bg-white/[0.12] hover:text-white'
                    : 'inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-[#0E9ED5] to-[#385CF2] px-7 py-4 text-sm font-bold uppercase tracking-[0.16em] text-white shadow-[0_20px_44px_-20px_rgba(14,158,213,0.95)] transition-all duration-300 hover:-translate-y-0.5'
                }
              >
                {action.label}
                {action.variant === 'secondary' ? null : <ArrowRightIcon className="ml-2 h-5 w-5" />}
              </Link>
            ))}
          </div>
        ) : null}

        {badges.length ? (
          <div className="mt-8 flex flex-wrap gap-3">
            {badges.map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-sm text-slate-200 backdrop-blur-xl"
              >
                <CheckCircleIcon className="h-4 w-4 text-cyan-300" />
                {badge}
              </span>
            ))}
          </div>
        ) : null}

        {metrics.length ? <div className="mt-10">{<MetricGrid items={metrics} tone="dark" />}</div> : null}
      </motion.div>

      {(panelTitle || panelItems.length) && (
        <motion.div {...reveal} transition={{ duration: 0.8, delay: 0.1 }} className="relative">
          <div className="overflow-hidden rounded-[32px] border border-white/[0.12] bg-white/[0.08] p-5 shadow-[0_40px_120px_-54px_rgba(14,158,213,0.55)] backdrop-blur-2xl sm:p-6">
            <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-5">
              {panelEyebrow ? (
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{panelEyebrow}</p>
              ) : null}
              {panelTitle ? <h2 className="mt-2 text-2xl font-semibold text-white">{panelTitle}</h2> : null}
              {panelDescription ? <p className="mt-3 text-sm leading-7 text-slate-300">{panelDescription}</p> : null}

              {panelItems.length ? (
                <div className="mt-6 space-y-3">
                  {panelItems.map((item) => (
                    <div key={item.title} className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
                      <div className="flex items-start gap-3">
                        <div className="rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] p-2.5">
                          <item.icon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">{item.title}</p>
                          <p className="mt-1 text-sm leading-6 text-slate-300">{item.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  </section>
);

export type PageHeroProps = React.ComponentProps<typeof StandardPageHero>;

export const PageHero: React.FC<PageHeroProps> = (props) =>
  process.env.REACT_APP_LANDING === 'warehouse'
    ? <WarehousePageHero {...props} />
    : <StandardPageHero {...props} />;

export const FaqAccordion: React.FC<{
  items: FaqItem[];
}> = ({ items }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="space-y-4">
      {items.map((item, index) => {
        const isOpen = openIndex === index;

        return (
          <motion.div
            key={item.question}
            {...reveal}
            transition={{ duration: 0.5, delay: index * 0.04 }}
            className="overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_20px_50px_-40px_rgba(15,23,42,0.28)]"
          >
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : index)}
              className="flex w-full items-center gap-4 px-6 py-5 text-left"
            >
              <span
                className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl text-sm font-bold ${
                  isOpen ? 'bg-[#385CF2] text-white' : 'bg-slate-100 text-slate-500'
                }`}
              >
                {index + 1}
              </span>
              <span className="flex-1 text-lg font-semibold text-slate-950">{item.question}</span>
              <motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25 }}>
                <ChevronDownIcon className="h-5 w-5 text-slate-500" />
              </motion.span>
            </button>

            <AnimatePresence initial={false}>
              {isOpen ? (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 pl-20">
                    <div className="rounded-[22px] border-l-4 border-cyan-400 bg-sky-50 px-5 py-4">
                      <div className="text-sm leading-7 text-slate-600">{item.answer}</div>
                    </div>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
};

export const PageCta: React.FC<{
  title: string;
  description: string;
  pills: string[];
  primaryHref?: string;
  primaryLabel?: string;
  secondaryHref?: string;
  secondaryLabel?: string;
}> = ({
  title,
  description,
  pills,
  primaryHref = '/contact-us',
  primaryLabel = 'Talk to experts',
  secondaryHref = '/services',
  secondaryLabel = 'Explore services',
}) => (
  <section className="bg-slate-950 px-4 pb-16 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-6xl rounded-[34px] border border-white/[0.12] bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 p-7 shadow-[0_34px_90px_-48px_rgba(15,23,42,0.9)] sm:p-10">
      <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">Next step</p>
          <h2 className="mt-4 font-display text-3xl font-bold text-white sm:text-4xl">{title}</h2>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">{description}</p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to={primaryHref}
              className="inline-flex items-center justify-center rounded-2xl bg-[#0E9ED5] px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-[#0b7aa6]"
            >
              {primaryLabel}
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </Link>
            <Link
              to={secondaryHref}
              className="inline-flex items-center justify-center rounded-2xl border border-white/[0.12] bg-white/[0.08] px-6 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-white/[0.12]"
            >
              {secondaryLabel}
            </Link>
          </div>
        </div>

        <div className="grid gap-3">
          {pills.slice(0, 3).map((pill) => (
            <div key={pill} className="rounded-[24px] border border-white/10 bg-white/[0.06] px-4 py-4 text-sm text-slate-200">
              {pill}
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
